import {MAX_STORAGE_SIZE} from '../consts/storage'

const MIN_BASE64_LENGTH = 100
const MAX_BASE64_LENGTH = Math.ceil((MAX_STORAGE_SIZE * 4) / 3)

export const validateStorageData = (data: string | null): string | null => {
  if (!data) return null
  if (data.length < MIN_BASE64_LENGTH || data.length > MAX_BASE64_LENGTH) return null
  const sizeInBytes = Math.ceil((data.length * 3) / 4)
  return sizeInBytes <= MAX_STORAGE_SIZE ? data : null
}

export const isValidDataUrl = (url: string): boolean => {
  if (!url) return false
  if (url.length < MIN_BASE64_LENGTH || url.length > MAX_BASE64_LENGTH) return false

  // Strict base64 format with proper padding
  const pattern =
    /^data:image\/(jpeg|jpg|png|webp);base64,([A-Za-z0-9+/]{4})*([A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/
  if (!pattern.test(url)) return false

  // Validate padding
  const base64 = url.split(',')[1]
  if (!base64) return false
  const padding = (base64.match(/=/g) || []).length
  if (padding > 2) return false

  // Block XSS patterns
  const xssPatterns = [/<script/i, /javascript:/i, /onerror=/i, /onload=/i, /<iframe/i]
  return !xssPatterns.some((p) => p.test(url))
}

export const isValidOcrData = (
  data: unknown
): data is {
  from_street1: string
  to_street1: string
  barcode_number?: string
} => {
  if (!data || typeof data !== 'object') return false
  const obj = data as Record<string, unknown>

  if (typeof obj.from_street1 !== 'string' || obj.from_street1.trim().length === 0) return false
  if (typeof obj.to_street1 !== 'string' || obj.to_street1.trim().length === 0) return false
  if (
    obj.barcode_number !== undefined &&
    (typeof obj.barcode_number !== 'string' || obj.barcode_number.trim().length === 0)
  )
    return false

  const MAX_ADDR_LEN = 500
  if (obj.from_street1.length > MAX_ADDR_LEN || obj.to_street1.length > MAX_ADDR_LEN) return false

  return true
}

export type ManifestStatus = 'paid' | 'pending' | 'expired' | 'fraud' | 'invalid'

export const normalizeStatus = (apiStatus?: string): 'paid' | 'pending' | 'invalid' => {
  if (!apiStatus) return 'invalid'

  const normalized = apiStatus.toLowerCase()
  if (normalized === 'paid') return 'paid'
  if (normalized === 'unpaid') return 'pending'

  return 'invalid'
}

export const STATUS_CONFIG = {
  paid: {color: 'success', titleKey: 'GORI_SIGNATURE_STATUS_PAID'},
  pending: {color: 'warning', titleKey: 'GORI_SIGNATURE_STATUS_PENDING'},
  expired: {color: 'danger', titleKey: 'GORI_SIGNATURE_STATUS_EXPIRED'},
  fraud: {color: 'danger', titleKey: 'GORI_SIGNATURE_STATUS_FRAUD'},
  invalid: {color: 'danger', titleKey: 'GORI_SIGNATURE_STATUS_INVALID'},
} as const

export const STATUS_ICON: Partial<Record<ManifestStatus, string>> = {
  paid: '/media/gori/gori-signature/icon-verified-outlined.svg',
  pending: '/media/gori/gori-signature/icon-pending-outlined.svg',
  fraud: '/media/gori/gori-signature/icon-fraud-outlined.svg',
  invalid: '/media/gori/gori-signature/icon-fraud-outlined.svg',
  expired: '/media/gori/gori-signature/icon-expired.svg',
}

export const STATUS_MESSAGE_KEY: Partial<Record<ManifestStatus, string>> = {
  pending: 'GORI_SIGNATURE_MESSAGE_PENDING',
  expired: 'GORI_SIGNATURE_MESSAGE_EXPIRED',
  fraud: 'GORI_SIGNATURE_MESSAGE_FRAUD',
  invalid: 'GORI_SIGNATURE_MESSAGE_FRAUD',
}
